import { useState } from 'react';
import { Lock } from 'lucide-react';
import {
  PRICE_FULL_REPORT,
  PRICE_UPGRADE_DELTA,
  MICRO_FULL_REPORT,
  COPY_UPGRADE_HEADLINE,
  COPY_CHECKOUT_NOTE,
  PRICE_KUNDLI_QUESTION,
  PRICE_KUNDLI_QUESTION_OLD,
} from '../constants/pricingCopy';
import StruckPrice from './StruckPrice';

/** Show “Read more” when question is long enough to need it */
const READ_MORE_THRESHOLD = 96;

/**
 * Upsell UI: lock card + compact question (read more for long text).
 * After payment: compact confirmation — never reveals a real generated answer.
 *
 * @param {'default'|'header'} placement — `header`: compact “Your Question” card; tap opens unlock modal (Full Report / upgrade). ₹149 + question: `DashboardPremiumPdfCard`.
 */
export default function PersonalAnswerTab({
  question,
  onGetReport,
  onGetPdfAddon,
  hasPaidReport,
  /** True when user has Dashboard unlock (₹149) but not Complete Report */
  hasDashboardUnlock = false,
  hasPdfAddon = false,
  show24hDeliveryMessage = false,
  placement = 'default',
  /** Opens in-dashboard Shastra AI (one-question) view */
  onNavigateToAskQuestion,
  /** Backend-verified: has this account NOT yet used its one free Shastra AI question? UI hint only. */
  freeQuestionAvailable = false,
}) {
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  /** Header placement: tap question → full unlock / Full Report modal */
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const q = (question && String(question).trim()) || '';
  const showReadMore = q.length > READ_MORE_THRESHOLD;

  /** Premium PDF — instant one-question answer is Shastra AI separately. */
  const UnlockUpsellPanel = ({ compact, onUnlockClick }) => {
    const reportPrice = hasDashboardUnlock ? PRICE_UPGRADE_DELTA : PRICE_FULL_REPORT;
    const reportBlurb = hasDashboardUnlock
      ? `Pay ${PRICE_UPGRADE_DELTA} more for the full premium PDF — total value ${PRICE_FULL_REPORT} (dashboard access already on file)`
      : `Full kundli analysis premium PDF report with detailed chart breakdown — ${PRICE_FULL_REPORT}`;
    return (
      <>
        <div
          className={`shrink-0 rounded-full bg-amber-900/90 shadow-lg ring-2 ring-amber-200/50 ${
            compact ? 'p-1.5 sm:p-2' : 'p-2 sm:p-2.5'
          }`}
        >
          <Lock
            className={`text-amber-50 ${compact ? 'h-5 w-5 sm:h-6 sm:w-6' : 'h-6 w-6 sm:h-7 sm:w-7'}`}
            strokeWidth={2.2}
            aria-hidden
          />
        </div>
        <p className={`shrink-0 font-bold tracking-tight text-amber-950 ${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'}`}>
          Premium PDF report
        </p>
        <p
          className={`max-w-[17rem] shrink-0 leading-snug text-amber-900/85 ${
            compact ? 'text-[10px] sm:text-[11px]' : 'text-[11px] sm:text-xs'
          }`}
        >
          {reportBlurb}
        </p>
        <p
          className={`max-w-[18rem] shrink-0 font-medium text-amber-950/90 ${
            compact ? 'text-[10px] sm:text-[11px]' : 'text-[11px] sm:text-xs'
          }`}
        >
          {freeQuestionAvailable ? (
            <>🎁 Pehla personal sawaal <strong>Shastra AI</strong> par FREE hai — yeh PDF alag product hai</>
          ) : (
            <>Ek personal sawaal ka turant jawab <strong>Shastra AI</strong> par — sirf <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} /> — yeh PDF alag product hai</>
          )}
        </p>
        <button
          type="button"
          onClick={onUnlockClick}
          className={`dashboard-cta-glow mt-0.5 inline-flex w-[min(100%,17rem)] shrink-0 flex-col items-center justify-center rounded-xl border border-amber-700/30 bg-gradient-to-r from-[#78350f] via-[#92400e] to-[#78350f] font-bold text-amber-50 shadow-[0_6px_20px_rgba(120,53,15,.35)] transition hover:brightness-105 ${
            compact ? 'px-3 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm' : 'px-4 py-2.5 text-sm sm:px-6 sm:py-3'
          }`}
        >
          {hasDashboardUnlock ? `Get premium PDF — ${reportPrice}` : `Order premium PDF — ${reportPrice}`}
        </button>
        <ul
          className={`mt-1.5 w-full max-w-[17rem] shrink-0 space-y-1 text-left ${
            compact ? 'text-[10px] sm:text-[11px]' : 'text-[11px] sm:text-xs'
          } text-amber-900/90`}
        >
          {MICRO_FULL_REPORT.map((line) => (
            <li key={line} className="flex gap-1.5 leading-snug">
              <span className="shrink-0 font-bold text-emerald-700">✔</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const questionCardClass =
    'w-full self-start rounded-xl border border-amber-200/85 bg-gradient-to-br from-amber-50/95 to-orange-50/85 p-3 text-left shadow-sm';

  const questionCardInner = (constrain, navigate) => {
    const inner = (
      <>
        <h2 className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-950 sm:text-sm">
          <span aria-hidden className="text-sm">
            ✨
          </span>
          Your Question
        </h2>
        <div className="border-l-2 border-amber-400 pl-2.5">
          {q ? (
            <>
              <p
                className={`text-sm leading-snug text-amber-950/95 ${showReadMore ? 'line-clamp-3' : ''}`}
                lang="en"
              >
                <span className="italic">“{q}”</span>
              </p>
              {showReadMore && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuestionModalOpen(true);
                  }}
                  className="mt-1 text-xs font-bold text-amber-800 underline decoration-amber-600/60 underline-offset-2 hover:text-amber-950"
                >
                  Read more
                </button>
              )}
            </>
          ) : (
            <p className="text-sm text-amber-700/80">—</p>
          )}
        </div>
      </>
    );

    const wide = constrain ? 'max-w-md md:max-w-lg' : '';

    if (typeof navigate === 'function') {
      return (
        <button
          type="button"
          onClick={navigate}
          className={`${questionCardClass} ${wide} cursor-pointer transition hover:ring-2 hover:ring-amber-400/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500`}
        >
          {inner}
        </button>
      );
    }

    return <div className={`${questionCardClass} ${wide}`}>{inner}</div>;
  };

  /** Dashboard header: question card + clear unlock CTA */
  const HeaderCombinedCard = () => (
    <div className="w-full max-w-[min(100%,22rem)] rounded-xl border border-amber-200/85 bg-gradient-to-br from-amber-50/98 to-orange-50/90 shadow-md sm:max-w-md">
      <div className="bg-amber-50/95 px-3 py-2.5 sm:px-3.5 sm:py-3">
        {q ? (
          <div className="space-y-1.5">
            {typeof onNavigateToAskQuestion === 'function' ? (
              <button
                type="button"
                onClick={onNavigateToAskQuestion}
                className="w-full rounded-lg text-left transition hover:ring-2 hover:ring-amber-400/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                <h2 className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-950 sm:text-xs">
                  <span aria-hidden className="text-xs sm:text-sm">
                    ✨
                  </span>
                  Your Question
                </h2>
                <div className="w-full rounded-lg border border-amber-200/80 bg-white/60 px-2.5 py-2">
                  <div className="border-l-2 border-amber-400 pl-2">
                    <p
                      className={`text-xs leading-snug text-amber-950/95 sm:text-sm ${showReadMore ? 'line-clamp-2' : ''}`}
                      lang="en"
                    >
                      <span className="italic">“{q}”</span>
                    </p>
                  </div>
                </div>
              </button>
            ) : (
              <>
                <h2 className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-950 sm:text-xs">
                  <span aria-hidden className="text-xs sm:text-sm">
                    ✨
                  </span>
                  Your Question
                </h2>
                <div className="w-full rounded-lg border border-amber-200/80 bg-white/60 px-2.5 py-2">
                  <div className="border-l-2 border-amber-400 pl-2">
                    <p
                      className={`text-xs leading-snug text-amber-950/95 sm:text-sm ${showReadMore ? 'line-clamp-2' : ''}`}
                      lang="en"
                    >
                      <span className="italic">“{q}”</span>
                    </p>
                  </div>
                </div>
              </>
            )}
            {typeof onNavigateToAskQuestion === 'function' ? (
              <button
                type="button"
                onClick={onNavigateToAskQuestion}
                className="w-full rounded-lg bg-gradient-to-r from-[#78350f] to-[#b45309] px-3 py-2 text-xs font-bold text-[#fffbeb] shadow-md transition hover:brightness-105 sm:text-sm"
              >
                {freeQuestionAvailable ? (
                  <>🎁 Get Your First Answer FREE</>
                ) : (
                  <>Unlock Your Answer → <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} className="text-[#fffbeb]" /></>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => (hasDashboardUnlock ? onGetReport?.() : setUnlockModalOpen(true))}
                className="w-full rounded-lg bg-gradient-to-r from-[#78350f] to-[#b45309] px-3 py-2 text-xs font-bold text-[#fffbeb] shadow-md transition hover:brightness-105 sm:text-sm"
              >
                {hasDashboardUnlock ? `Get premium PDF — ${PRICE_UPGRADE_DELTA}` : `Order premium PDF — ${PRICE_FULL_REPORT}`}
              </button>
            )}
            {typeof onNavigateToAskQuestion === 'function' && (
              <button
                type="button"
                onClick={() => (hasDashboardUnlock ? onGetReport?.() : setUnlockModalOpen(true))}
                className="w-full rounded-lg border border-amber-300/80 bg-amber-50/70 px-3 py-1.5 text-[10px] font-medium text-amber-900/95 shadow-sm transition hover:bg-amber-100/90 sm:text-xs"
              >
                {hasDashboardUnlock
                  ? `Premium PDF — +${PRICE_UPGRADE_DELTA}`
                  : `Premium PDF report — ${PRICE_FULL_REPORT}`}
              </button>
            )}
            {showReadMore && (
              <button
                type="button"
                onClick={() => setQuestionModalOpen(true)}
                className="text-[10px] font-bold text-amber-800 underline decoration-amber-600/60 underline-offset-2 hover:text-amber-950 sm:text-xs"
              >
                Read full question
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-amber-700/80">—</p>
        )}
      </div>
    </div>
  );

  if (placement === 'header') {
    if (hasPaidReport || !q) return null;
    return (
      <>
        <HeaderCombinedCard />
        {unlockModalOpen && (
          <div
            className="fixed inset-0 z-[205] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unlock-answer-title"
            onClick={() => setUnlockModalOpen(false)}
          >
            <div
              className="max-h-[min(90vh,640px)] w-full max-w-md overflow-y-auto rounded-2xl border-2 border-amber-200/90 bg-[#fffbeb] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-2 border-b border-amber-200/80 bg-amber-50/95 px-4 py-3">
                <h3 id="unlock-answer-title" className="text-base font-bold text-amber-950">
                  Full kundli analysis — premium PDF
                </h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setUnlockModalOpen(false)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg leading-none text-amber-900 hover:bg-amber-200"
                >
                  ×
                </button>
              </div>
              {q ? (
                <p className="border-b border-amber-100/90 px-4 py-2.5 text-sm italic leading-snug text-amber-900/95">“{q}”</p>
              ) : null}
              <div
                className="flex flex-col items-center gap-1.5 px-3 pb-5 pt-4 text-center sm:gap-2 sm:px-5 sm:pt-5"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255,251,235,0.85) 0%, rgba(254,243,199,0.88) 50%, rgba(254,215,170,0.82) 100%)',
                }}
              >
                <UnlockUpsellPanel
                  compact
                  onUnlockClick={() => {
                    setUnlockModalOpen(false);
                    onGetReport?.();
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {questionModalOpen && q && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="question-modal-title"
            onClick={() => setQuestionModalOpen(false)}
          >
            <div
              className="max-h-[min(70vh,520px)] w-full max-w-md overflow-y-auto rounded-2xl border border-amber-200 bg-[#fffbeb] p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="question-modal-title" className="mb-3 text-base font-bold text-amber-950">
                Your question
              </h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-amber-900">“{q}”</p>
              <button
                type="button"
                onClick={() => setQuestionModalOpen(false)}
                className="mt-5 w-full rounded-xl bg-gradient-to-r from-amber-700 to-orange-700 py-2.5 text-sm font-bold text-white shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (!hasPaidReport) {
    return null;
  }

  return (
    <div className="w-full space-y-3 text-left md:space-y-4">
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 md:gap-4">
        {questionCardInner(true, onNavigateToAskQuestion)}
        <div
          role="status"
          className="rounded-xl border border-emerald-300/80 bg-gradient-to-br from-emerald-50 to-teal-50/85 p-4 shadow-sm"
        >
          <p className="mb-1.5 flex flex-wrap items-center gap-2 text-sm font-bold text-emerald-950">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs text-white">
              ✓
            </span>
            {show24hDeliveryMessage
              ? `Premium PDF (${PRICE_FULL_REPORT}) — confirmed`
              : `Premium PDF (${PRICE_FULL_REPORT}) — purchased`}
          </p>
          {show24hDeliveryMessage ? (
            <p className="text-xs leading-relaxed text-emerald-900/95 sm:text-sm">
              Your <strong>premium PDF report</strong> will be delivered <strong>within 24 hours</strong> to your registered
              email. {freeQuestionAvailable ? (
                <>🎁 Turant Q&amp;A ke liye <strong>Shastra AI</strong> ka pehla sawaal FREE hai.</>
              ) : (
                <>Turant Q&amp;A ke liye <strong>Shastra AI</strong> (<StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />) use karein.</>
              )}
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-emerald-900/95 sm:text-sm">
              Your <strong>premium PDF order ({PRICE_FULL_REPORT})</strong> is active. {freeQuestionAvailable ? (
                <>🎁 Turant ek sawaal <strong>Shastra AI</strong> tab par FREE hai.</>
              ) : (
                <>Turant ek sawaal ke liye <strong>Shastra AI</strong> (<StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />) tab open karein.</>
              )}
            </p>
          )}
        </div>
      </div>

      {questionModalOpen && q && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="question-modal-title"
          onClick={() => setQuestionModalOpen(false)}
        >
          <div
            className="max-h-[min(70vh,520px)] w-full max-w-md overflow-y-auto rounded-2xl border border-amber-200 bg-[#fffbeb] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="question-modal-title" className="mb-3 text-base font-bold text-amber-950">
              Your question
            </h3>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-amber-900">“{q}”</p>
            <button
              type="button"
              onClick={() => setQuestionModalOpen(false)}
              className="mt-5 w-full rounded-xl bg-gradient-to-r from-amber-700 to-orange-700 py-2.5 text-sm font-bold text-white shadow-md"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
